import { z } from "zod";

// ---------------------------------------------------------------------------
// Behavioral Condition Evaluators — interfaces for C.12 implementation
// ---------------------------------------------------------------------------
// These define the contract for conditions that require external data lookups
// (ClickHouse queries, segment membership, time checks). The actual
// implementations are wired in C.12 (behavioral) and C.13 (segments).
//
// All evaluators use dependency injection for data sources — no direct DB
// or ClickHouse imports. This allows mock implementations in tests.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Behavioral conditions (require ClickHouse engagement data)
// ---------------------------------------------------------------------------

export const behavioralConditionSchema = z.object({
  type: z.enum(["opened_email_in_last", "clicked_link_in_last"]),
  days: z.number().int().positive().max(365),
});

export type BehavioralCondition = z.infer<typeof behavioralConditionSchema>;

/** Query function for engagement data — injected, not imported */
export interface EngagementQueryFn {
  (tenantId: string, contactId: string, eventType: string, sinceDaysAgo: number): Promise<number>;
}

export interface BehavioralConditionEvaluator {
  evaluate(
    tenantId: string,
    contactId: string,
    condition: BehavioralCondition,
  ): Promise<boolean>;
}

export function createBehavioralConditionEvaluator(
  deps: { queryEngagement: EngagementQueryFn },
): BehavioralConditionEvaluator {
  return {
    async evaluate(tenantId, contactId, condition) {
      const eventType = condition.type === "opened_email_in_last"
        ? "email.opened"
        : "email.clicked";
      const count = await deps.queryEngagement(tenantId, contactId, eventType, condition.days);
      return count > 0;
    },
  };
}

// ---------------------------------------------------------------------------
// Segment membership condition
// ---------------------------------------------------------------------------

export const segmentConditionSchema = z.object({
  type: z.literal("in_segment"),
  segmentId: z.string().uuid(),
});

export type SegmentCondition = z.infer<typeof segmentConditionSchema>;

/** Segment membership check — injected, not imported */
export interface SegmentMembershipFn {
  (tenantId: string, contactId: string, segmentId: string): Promise<boolean>;
}

export interface SegmentConditionEvaluator {
  evaluate(
    tenantId: string,
    contactId: string,
    condition: SegmentCondition,
  ): Promise<boolean>;
}

export function createSegmentConditionEvaluator(
  deps: { checkMembership: SegmentMembershipFn },
): SegmentConditionEvaluator {
  return {
    async evaluate(tenantId, contactId, condition) {
      return deps.checkMembership(tenantId, contactId, condition.segmentId);
    },
  };
}

// ---------------------------------------------------------------------------
// Time-based conditions (pure logic — no external data)
// ---------------------------------------------------------------------------

export const dayOfWeekConditionSchema = z.object({
  type: z.literal("day_of_week"),
  days: z.array(z.number().int().min(0).max(6)), // 0=Sunday, 6=Saturday
  timezone: z.string().optional(), // IANA timezone, defaults to UTC
});

export type DayOfWeekCondition = z.infer<typeof dayOfWeekConditionSchema>;

export const timeOfDayConditionSchema = z.object({
  type: z.literal("time_of_day"),
  startHour: z.number().int().min(0).max(23),
  startMinute: z.number().int().min(0).max(59).default(0),
  endHour: z.number().int().min(0).max(23),
  endMinute: z.number().int().min(0).max(59).default(0),
  timezone: z.string().optional(), // IANA timezone, defaults to UTC
});

export type TimeOfDayCondition = z.infer<typeof timeOfDayConditionSchema>;

export interface TimeConditionEvaluator {
  evaluateDayOfWeek(condition: DayOfWeekCondition, now?: Date): boolean;
  evaluateTimeOfDay(condition: TimeOfDayCondition, now?: Date): boolean;
}

export function createTimeConditionEvaluator(): TimeConditionEvaluator {
  return {
    evaluateDayOfWeek(condition, now = new Date()) {
      const tz = condition.timezone ?? "UTC";
      const dayInTz = getDayInTimezone(now, tz);
      return condition.days.includes(dayInTz);
    },

    evaluateTimeOfDay(condition, now = new Date()) {
      const tz = condition.timezone ?? "UTC";
      const { hour, minute } = getTimeInTimezone(now, tz);
      const currentMinutes = hour * 60 + minute;
      const startMinutes = condition.startHour * 60 + (condition.startMinute ?? 0);
      const endMinutes = condition.endHour * 60 + (condition.endMinute ?? 0);

      if (startMinutes <= endMinutes) {
        // Same-day range: 9:00-17:00
        return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
      }
      // Overnight range: 22:00-06:00
      return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
    },
  };
}

// ---------------------------------------------------------------------------
// Timezone helpers
// ---------------------------------------------------------------------------

function getDayInTimezone(date: Date, timezone: string): number {
  const formatted = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    timeZone: timezone,
  }).format(date);
  const dayMap: Record<string, number> = {
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
  };
  return dayMap[formatted] ?? 0;
}

function getTimeInTimezone(date: Date, timezone: string): { hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: false,
    timeZone: timezone,
  }).formatToParts(date);

  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
  return { hour, minute };
}
